import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {fromEvent, merge, Subject, Subscription, timer} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {DataService} from '../data-service/data.service';
import {ToastService} from '../toast-service/toast.service';

@Injectable({providedIn: 'root'})
export class InactivityService implements OnDestroy {

  private readonly timeoutMs  = environment.sessionTimeoutMinutes * 60 * 1000;
  private readonly warningMs  = environment.sessionWarningMinutes * 60 * 1000;
  private readonly warnAfterMs = this.timeoutMs - this.warningMs;

  private stop$         = new Subject<void>();
  private warningSub    : Subscription | null = null;
  private timeoutSub    : Subscription | null = null;
  private warningShown  = false;

  // All events that count as user activity
  private readonly activityEvents$ = merge(
    fromEvent(document, 'mousemove'),
    fromEvent(document, 'keydown'),
    fromEvent(document, 'click'),
    fromEvent(document, 'touchstart'),
    fromEvent(document, 'scroll'),
  );

  constructor(
    private dataService: DataService,
    private toastService: ToastService,
    private ngZone: NgZone,
  ) {}

  /**
   * Start watching for inactivity. Call this once after a successful login.
   * Runs outside Angular's zone so mousemove/scroll don't trigger change detection.
   */
  start(): void {
    this.stop();   // clear any previous session

    this.ngZone.runOutsideAngular(() => {
      this.activityEvents$
        .pipe(
          // Every activity event restarts both timers
          switchMap(() => {
            this.warningShown = false;
            this.cancelTimers();
            this.scheduleWarning();
            this.scheduleLogout();
            // Return an observable that completes immediately — switchMap just drives the reset
            return new Subject<void>();
          }),
          takeUntil(this.stop$),
        )
        .subscribe();

      // Kick off the initial timers without waiting for the first event
      this.scheduleWarning();
      this.scheduleLogout();
    });
  }

  /**
   * Stop watching. Call this on logout or when navigating to the login page.
   */
  stop(): void {
    this.stop$.next();
    this.cancelTimers();
    this.warningShown = false;
  }

  private scheduleWarning(): void {
    this.warningSub = timer(this.warnAfterMs).subscribe(() => {
      if (!this.isLoggedIn()) return;
      if (!this.warningShown) {
        this.warningShown = true;
        this.ngZone.run(() => {
          this.toastService.showWarning(
            `You will be logged out in ${environment.sessionWarningMinutes} minute(s) due to inactivity.`,
            'Session Expiring',
          );
        });
      }
    });
  }

  private scheduleLogout(): void {
    this.timeoutSub = timer(this.timeoutMs).subscribe(() => {
      if (!this.isLoggedIn()) return;
      this.ngZone.run(() => {
        this.toastService.showError('You have been logged out due to inactivity.', 'Session Expired');
        this.dataService.logoutUser();
      });
    });
  }

  private cancelTimers(): void {
    this.warningSub?.unsubscribe();
    this.timeoutSub?.unsubscribe();
    this.warningSub  = null;
    this.timeoutSub  = null;
  }

  private isLoggedIn(): boolean {
    return !!this.dataService.getUserToken();
  }

  ngOnDestroy(): void {
    this.stop();
    this.stop$.complete();
  }
}
